// src/screens/VideoWatchScreen.js
// ============================================================================
// VIDEO WATCH PAGE — public playback for published profile videos (/v/:id)
//
// Videos system (approved 2026-07-07). Playback = the session's recorded
// 5-min segments played sequentially (same material the Evidence Viewer
// uses); signed URLs come from getVideo. Anyone can watch (logged-out OK,
// same policy as EventWatch); likes/comments/views need sign-in.
//
// View + watch-through metering (signed-in only, spoof-proof):
//   - startVideoWatch callable creates the videoAudience roster doc and
//     counts the distinct-viewer view once
//   - a 20s heartbeat then updates lastSeenAt/watchSeconds directly; the
//     firestore rules meter it to wall-clock (≥15s gap, ≤+25s per beat)
// A signed-out visit calls startVideoWatch once with a per-visit
// anonSessionId: counted for the creator's audience numbers only (audience
// metrics AM-1), no heartbeat, never a view.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, TextInput, ActivityIndicator,
  StyleSheet, Platform, Share, Image,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, updateDoc, serverTimestamp, increment } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebaseConfig';
import Header from '../components/Header';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';
import JoinMembershipModal from '../components/JoinMembershipModal';
import { useMyChannelMembership, membershipErrorMessage } from '../utils/membershipPayment';
import {
  isTierKey, videoTierLockText, SWITCH_TIER_LABEL, memberCanSwitchNow, pendingSwitchReaches,
} from '../utils/membershipTiers';

const IS_WEB = Platform.OS === 'web';
const HEARTBEAT_MS = 20 * 1000;
// Members-only videos (3.5a): after an unlock reload that still answers
// members-only (the server can trail the pair doc by a moment), retry once.
const UNLOCK_RETRY_MS = 2000;

// The members-only refusal ("members-only: ..."), from getVideo (with
// details), or from startVideoWatch / likeVideo / addVideoComment (none).
const isMembersOnlyError = (err) => String(err?.message || '').includes('members-only:');

// Per-visit id for a signed-out visit (same shape as StreamWatchScreen's),
// minted once per video watched.
const newAnonSessionId = () => `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

// A getVideo members-only refusal -> the locked page. kind: 'join' (a Join
// here unlocks it), 'noperk' (the viewer's membership doesn't include
// members-only videos), 'tier' (R5: a live member whose tier is below
// the video's minimum, details.memberTierLow + minTierKey; "Switch tier"
// only when details.joinable says a tier that is on reaches it, ADV2-2, and
// no pending switch already reaches it, R3C-REV-2) or
// 'unavailable' (nobody can join for it right now). minTierKey (R5) is kept
// for every kind: the server names the tier that unlocks a video (never
// below the members-only videos perk's start) when it is above Spark, so a
// Join names it (F7) and preselects a tier that unlocks it.
const lockFromError = (err) => {
  const d = err?.details || {};
  if (d.memberTierLow === true && isTierKey(d.minTierKey)) {
    return {
      ownerUid: typeof d.ownerUid === 'string' && d.ownerUid ? d.ownerUid : null,
      ownerName: typeof d.ownerName === 'string' && d.ownerName ? d.ownerName : null,
      title: typeof d.title === 'string' ? d.title : '',
      thumbnailUrl: typeof d.thumbnailUrl === 'string' ? d.thumbnailUrl : null,
      kind: 'tier',
      joinable: d.joinable === true,
      minTierKey: d.minTierKey,
      sentence: String(err?.message || '').replace(/^[a-z][a-z-]*:\s*/, ''),
    };
  }
  return {
    ownerUid: typeof d.ownerUid === 'string' && d.ownerUid ? d.ownerUid : null,
    ownerName: typeof d.ownerName === 'string' && d.ownerName ? d.ownerName : null,
    title: typeof d.title === 'string' ? d.title : '',
    thumbnailUrl: typeof d.thumbnailUrl === 'string' ? d.thumbnailUrl : null,
    kind: d.joinable === true ? 'join' : (d.memberNoPerk === true ? 'noperk' : 'unavailable'),
    minTierKey: isTierKey(d.minTierKey) ? d.minTierKey : null,
    // The server's own sentence, for a refusal that somehow lacks a name.
    sentence: String(err?.message || '').replace(/^[a-z][a-z-]*:\s*/, ''),
  };
};

const fmtDuration = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};

const fmtCount = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - Date.parse(iso)) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function VideoWatchScreen({ route, navigation }) {
  const videoId = route?.params?.videoId || null;
  const { showError } = useError();

  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partIndex, setPartIndex] = useState(0);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [busy, setBusy] = useState(null);
  // The video whose watch start already ran: { videoId, started }, where
  // `started` resolves to the uid to heartbeat (null = signed out, or the
  // start failed). Per video, not per mount (see the metering effect).
  const meteredFor = useRef(null);
  const videoRef = useRef(null);
  const autoplayed = useRef(false);
  const isFocused = useIsFocused();
  // Members-only (3.5a). `lock` = getVideo refused with members-only (the
  // locked page); `interactionsLocked` = a playing video whose like or
  // comment was refused (Like and the comment box hide). `unlockUid` is the
  // channel whose pair doc we watch meanwhile; it only changes when the lock
  // starts or a load succeeds, so the listener survives an unlock reload.
  const [lock, setLock] = useState(null);
  const [interactionsLocked, setInteractionsLocked] = useState(false);
  const [unlockUid, setUnlockUid] = useState(null);
  const [joinOpenFor, setJoinOpenFor] = useState(null);

  // quiet = an unlock reload: keep the locked page on screen (no spinner) so
  // a Join modal mounted over it keeps its welcome.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!videoId) { setLoading(false); return 'error'; }
    if (!quiet) setLoading(true);
    const functions = getFunctions();

    // Comments render below the fold. Fire the request now (so it still runs in
    // parallel) but DON'T await it for the spinner — a cold getVideoComments
    // used to add a whole second cold start to time-to-player. The spinner now
    // waits on getVideo alone; comments land whenever they land.
    httpsCallable(functions, 'getVideoComments')({ videoId })
      .then((res) => setComments(res.data?.comments || []))
      .catch(() => { /* not found / empty — leave comments as [] */ });

    try {
      const videoRes = await httpsCallable(functions, 'getVideo')({ videoId });
      setVideo(videoRes.data);
      setLock(null);
      setInteractionsLocked(false);
      setUnlockUid(null);
      return 'ok';
    } catch (err) {
      setVideo(null);
      if (isMembersOnlyError(err)) {
        // Keep the refusal's details (the comments call above is refused
        // with the same token; its catch leaves comments empty and the
        // locked page shows none).
        const next = lockFromError(err);
        setLock(next);
        setUnlockUid(next.ownerUid);
        return 'locked';
      }
      console.error('[VideoWatch] load failed:', err);
      setLock(null);
      setUnlockUid(null);
      return 'error';
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  useEffect(() => { load(); }, [load]);

  // Unlock without a reload. While locked (or while a playing video's
  // interactions are locked), listen to the viewer's own pair doc for that
  // channel. On its isMember false -> true edge (the join's welcome, a
  // "Done" during "Finishing up", a join in another tab): a locked page
  // clears and loads again; a playing video only gets Like and comments
  // back (a playing video is never reloaded).
  const m = useMyChannelMembership(unlockUid);
  const memberNow = useRef(false);
  memberNow.current = m.isMember === true;
  const prevMember = useRef(null); // null = not settled yet for this uid
  const unlocking = useRef(false);
  const aliveKey = useRef(0);
  useEffect(() => { aliveKey.current += 1; }, [videoId]);
  useEffect(() => { prevMember.current = null; }, [unlockUid]);

  const unlockAndReload = useCallback(async () => {
    if (unlocking.current) return;
    unlocking.current = true;
    const key = aliveKey.current;
    try {
      let res = await load({ quiet: true });
      if (res === 'locked' && key === aliveKey.current) {
        await new Promise((r) => setTimeout(r, UNLOCK_RETRY_MS));
        if (key !== aliveKey.current) return;
        res = await load({ quiet: true });
        // Still refused after the retry: while the pair doc says member,
        // never offer Join again; otherwise the server's copy stands.
        if (res === 'locked' && key === aliveKey.current && memberNow.current) {
          setLock((l) => (l && l.kind !== 'tier' ? { ...l, kind: 'noperk' } : l));
        }
      }
    } finally {
      unlocking.current = false;
    }
  }, [load]);

  useEffect(() => {
    if (!unlockUid || m.loading || m.error) return;
    const prev = prevMember.current;
    prevMember.current = m.isMember === true;
    if (m.isMember !== true || prev === true) return;
    if (lock) {
      // A first settled read that is already a member only matters when
      // the server offered a Join (a join that landed just before the
      // listener attached); a no-perk member is already shown as one.
      if (prev === null && (lock.kind === 'noperk' || lock.kind === 'tier')) return;
      unlockAndReload();
    } else if (interactionsLocked && prev !== null) {
      // Only a real edge: a member refused anyway (no video perk) whose
      // first read is already "member" keeps the interactions locked.
      setInteractionsLocked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlockUid, m.loading, m.error, m.isMember]);

  // A tier lock (R5) clears when the member's tier changes (a switch takes
  // effect at their renewal): load again once the pair doc's tier moves.
  const memberTier = m.doc?.tierKey || null;
  const lockedAtTier = useRef(undefined);
  useEffect(() => {
    if (lock?.kind !== 'tier' || m.loading || m.error) { lockedAtTier.current = undefined; return; }
    if (lockedAtTier.current === undefined) { lockedAtTier.current = memberTier; return; }
    if (memberTier !== lockedAtTier.current) {
      lockedAtTier.current = memberTier;
      unlockAndReload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lock?.kind, m.loading, m.error, memberTier]);

  // A like or comment refused as members-only on a playing video.
  const lockInteractions = () => {
    setInteractionsLocked(true);
    if (video?.owner?.uid) setUnlockUid(video.owner.uid);
  };

  // Tiers (R5): the modal opens at its switch stage for a tier lock.
  const [joinOpenTo, setJoinOpenTo] = useState(null);
  const openJoin = () => {
    if (!getAuth().currentUser) { navigation.navigate('SignUp'); return; }
    setJoinOpenTo(lock?.kind === 'tier' ? 'switch' : null);
    if (lock?.ownerUid) setJoinOpenFor(lock.ownerUid);
  };

  // Mounted at screen level in every branch (same position, keyed by the
  // resolved channel: the lock's owner, then the loaded video's), so an
  // unlock reload never tears down a payment form or its welcome; a
  // different channel unmounts it with its form.
  const joinUid = lock?.ownerUid || (video && !video.isOwner ? video.owner?.uid : null) || null;
  const joinModal = joinUid ? (
    <JoinMembershipModal
      key={'join:' + joinUid}
      creatorUid={joinUid}
      creatorName={lock?.ownerName || video?.owner?.userName || ''}
      visible={joinOpenFor === joinUid}
      openTo={joinOpenTo}
      minTierKey={lock?.minTierKey || null}
      gatePerk={lock ? 'membersVideos' : null}
      onClose={() => { setJoinOpenFor(null); setJoinOpenTo(null); }}
      navigation={navigation}
    />
  ) : null;

  // Watch metering: signed-in viewers heartbeat the roster; a signed-out visit
  // is counted once for the creator's audience numbers (no heartbeat, never a view).
  // Keyed by the video, not the screen mount: React Navigation reuses this
  // screen from video A to video B, so each new video gets its own start call
  // (and its own per-visit anon id), and only once the loaded video IS the
  // route's (a stale A is never recorded under B). A like or comment replaces
  // `video` but not its id, so the effect doesn't re-run and the heartbeat
  // keeps going; a re-run for the same video never calls start again.
  const meterVideoId = video && video.id === videoId ? videoId : null;
  const meterIsOwner = video?.isOwner === true;
  // Leaving a video ends its visit: coming back to it (A -> B -> A) is a new one.
  useEffect(() => { if (meteredFor.current?.videoId !== videoId) meteredFor.current = null; }, [videoId]);
  useEffect(() => {
    if (!meterVideoId) return undefined;
    if (meteredFor.current?.videoId !== meterVideoId) {
      const anonSessionId = newAnonSessionId();
      meteredFor.current = {
        videoId: meterVideoId,
        started: (async () => {
          // Wait for a restored session so a signed-in viewer is never metered as signed out.
          try { await getAuth().authStateReady?.(); } catch (_) { /* fall through with currentUser as is */ }
          const uid = getAuth().currentUser?.uid;
          if (!uid) {
            if (!meterIsOwner) {
              httpsCallable(getFunctions(), 'startVideoWatch')({ videoId: meterVideoId, anonSessionId }).catch(() => {});
            }
            return null;
          }
          try {
            await httpsCallable(getFunctions(), 'startVideoWatch')({ videoId: meterVideoId });
          } catch (e) { return null; /* not published / not found — don't heartbeat */ }
          return uid;
        })(),
      };
    }

    let cancelled = false;
    let timer = null;
    meteredFor.current.started.then((uid) => {
      if (cancelled || !uid) return;
      const rosterRef = doc(db, 'videoAudience', meterVideoId, 'viewers', uid);
      timer = setInterval(() => {
        updateDoc(rosterRef, {
          lastSeenAt: serverTimestamp(),
          watchSeconds: increment(20),
        }).catch(() => { /* rules meter to wall-clock — denied beats drop */ });
      }, HEARTBEAT_MS);
    });

    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [meterVideoId, meterIsOwner]);

  // Pause when the screen loses focus. Navigating away via the header nav does
  // NOT unmount this screen, so the <video> kept playing (audio bled into the
  // next page) until a refresh (founder bug 2026-07-25).
  useEffect(() => {
    if (!isFocused && videoRef.current) {
      try { videoRef.current.pause(); } catch (_) { /* element gone */ }
    }
  }, [isFocused]);

  // Autoplay ONCE on load: try with sound; if the browser blocks it (no media
  // engagement yet), fall back to muted autoplay so it never dead-stops.
  useEffect(() => {
    if (!IS_WEB || autoplayed.current || !isFocused) return;
    const el = videoRef.current;
    const url = video?.parts?.[partIndex]?.url;
    if (!el || !url) return;
    autoplayed.current = true;
    const tryPlay = () => {
      const p = el.play();
      if (p && p.catch) p.catch(() => { el.muted = true; el.play().catch(() => {}); });
    };
    if (el.readyState >= 2) tryPlay();
    else el.addEventListener('canplay', tryPlay, { once: true });
  }, [isFocused, video, partIndex]);

  const toggleLike = async () => {
    if (!getAuth().currentUser) {
      showError('Sign in required', 'Sign in to like videos.');
      return;
    }
    setBusy('like');
    try {
      const res = await httpsCallable(getFunctions(), 'likeVideo')({ videoId });
      setVideo((v) => v && ({
        ...v,
        liked: res.data.liked,
        likes: Math.max(0, (v.likes || 0) + (res.data.liked ? 1 : -1)),
      }));
    } catch (err) {
      if (isMembersOnlyError(err)) lockInteractions();
      showError('Could not like video', membershipErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const shareVideo = async () => {
    const url = `https://critcash.com/v/${videoId}`;
    try {
      if (IS_WEB && navigator?.clipboard) {
        await navigator.clipboard.writeText(url);
        showError('Link copied', 'Video link copied to your clipboard.');
      } else {
        await Share.share({ message: url });
      }
    } catch (e) { /* user cancelled */ }
  };

  const submitComment = async () => {
    if (!getAuth().currentUser) {
      showError('Sign in required', 'Sign in to comment.');
      return;
    }
    const text = commentText.trim();
    if (!text) return;
    setBusy('comment');
    try {
      await httpsCallable(getFunctions(), 'addVideoComment')({ videoId, text });
      setCommentText('');
      const res = await httpsCallable(getFunctions(), 'getVideoComments')({ videoId });
      setComments(res.data?.comments || []);
      setVideo((v) => v && ({ ...v, comments: (v.comments || 0) + 1 }));
    } catch (err) {
      if (isMembersOnlyError(err)) lockInteractions();
      showError('Could not post comment', membershipErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const submitReport = async (category) => {
    const reason = IS_WEB
      ? window.prompt(category === 'copyright'
        ? 'Describe the copyrighted work and who owns it (optional):'
        : 'Add any details (optional):')
      : 'Reported from app';
    if (reason === null) return; // user cancelled the prompt
    try {
      await httpsCallable(getFunctions(), 'reportVideo')({ videoId, category, reason });
      showError('Report sent', category === 'copyright'
        ? 'Thanks — the video is flagged and our team will review the claim.'
        : 'Thanks — our team will review it.');
    } catch (err) {
      showError('Could not send report', err?.message || 'Please try again.');
    }
  };

  const reportThis = () => {
    if (!getAuth().currentUser) {
      showError('Sign in required', 'Sign in to report content.');
      return;
    }
    showError('Report this video', 'What’s the issue?', [
      { text: 'Copyright infringement', onPress: () => submitReport('copyright') },
      { text: 'Inappropriate content', onPress: () => submitReport('inappropriate') },
      { text: 'Spam or misleading', onPress: () => submitReport('spam') },
      { text: 'Something else', onPress: () => submitReport('other') },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  if (loading) {
    return (
      <View style={styles.screen}>
        <Header navigation={navigation} />
        <View style={styles.center}><ActivityIndicator size="large" color="#FCD000" /></View>
        <NavBar navigation={navigation} />
        {joinModal}
      </View>
    );
  }

  // Members-only and this viewer can't watch it (3.5a): the title and
  // thumbnail are public; Join only when a join would unlock it.
  if (!video && lock) {
    // F7 for a Join too when the video needs a tier above Spark (a Join of a
    // lower tier would not unlock it); today's sentence otherwise.
    const joinTier = lock.kind === 'join' && isTierKey(lock.minTierKey) && lock.minTierKey !== 'spark' ? lock.minTierKey : null;
    const lockMsg = lock.kind === 'join'
      ? (lock.ownerName
        ? (joinTier ? videoTierLockText(joinTier, lock.ownerName) : `This video is for members of ${lock.ownerName}'s channel.`)
        : (lock.sentence || "This video isn't available to watch right now."))
      : lock.kind === 'tier'
        ? (lock.ownerName
          ? videoTierLockText(lock.minTierKey, lock.ownerName)
          : (lock.sentence || "This video isn't available to watch right now."))
        : lock.kind === 'noperk'
          ? "Members-only videos aren't part of your membership right now."
          : "This video isn't available to watch right now.";
    return (
      <View style={styles.screen}>
        <Header navigation={navigation} />
        <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
          <TouchableOpacity
            style={styles.backLink}
            onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
          >
            <Ionicons name="chevron-back" size={18} color="#FCD000" />
            <Text style={styles.backLinkTxt}>Back</Text>
          </TouchableOpacity>
          <View style={styles.lockedThumb}>
            {lock.thumbnailUrl ? (
              <Image source={{ uri: lock.thumbnailUrl }} style={styles.lockedThumbImg} resizeMode="cover" />
            ) : null}
            <View style={styles.lockedBadge}>
              <Ionicons name="lock-closed" size={22} color="#FCD000" />
            </View>
          </View>
          {!!lock.title && <Text style={styles.title}>{lock.title}</Text>}
          <Text style={styles.lockedMsg}>{lockMsg}</Text>
          {lock.kind === 'join' ? (
            <TouchableOpacity style={styles.joinBtn} onPress={openJoin} accessibilityRole="button">
              <Ionicons name="star-outline" size={15} color="#000" />
              <Text style={styles.joinBtnTxt}>Join</Text>
            </TouchableOpacity>
          ) : lock.kind === 'tier' && lock.joinable === true && memberCanSwitchNow(m.doc) && !pendingSwitchReaches(m.doc, lock.minTierKey) ? (
            <TouchableOpacity style={styles.joinBtn} onPress={openJoin} accessibilityRole="button">
              <Ionicons name="swap-vertical" size={15} color="#000" />
              <Text style={styles.joinBtnTxt}>{SWITCH_TIER_LABEL}</Text>
            </TouchableOpacity>
          ) : null}
        </ScrollView>
        <NavBar navigation={navigation} />
        {joinModal}
      </View>
    );
  }

  if (!video) {
    return (
      <View style={styles.screen}>
        <Header navigation={navigation} />
        <View style={styles.center}>
          <Text style={styles.emptyTxt}>This video isn&apos;t available.</Text>
          <TouchableOpacity
            style={styles.backBtn}
            onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
          >
            <Text style={styles.backBtnTxt}>BACK</Text>
          </TouchableOpacity>
        </View>
        <NavBar navigation={navigation} />
        {joinModal}
      </View>
    );
  }

  const parts = video.parts || [];
  const currentPart = parts[partIndex] || null;

  return (
    <View style={styles.screen}>
      <Header navigation={navigation} />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <TouchableOpacity
          style={styles.backLink}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('PublicProfile', { uid: video.owner?.uid }))}
        >
          <Ionicons name="chevron-back" size={18} color="#FCD000" />
          <Text style={styles.backLinkTxt}>Back</Text>
        </TouchableOpacity>

        {/* Player — sequential segment playback (parts auto-advance) */}
        <View style={styles.playerWrap}>
          {IS_WEB && currentPart ? (
            <video
              ref={videoRef}
              key={currentPart.name}
              src={currentPart.url}
              controls
              playsInline
              autoPlay={partIndex > 0}
              style={{ width: '100%', aspectRatio: '16/9', backgroundColor: '#000', borderRadius: 10 }}
              onEnded={() => {
                if (partIndex < parts.length - 1) setPartIndex(partIndex + 1);
              }}
            />
          ) : (
            <View style={styles.playerFallback}>
              <Text style={styles.playerFallbackTxt}>Video playback isn&apos;t available here.</Text>
            </View>
          )}
          {parts.length > 1 && (
            <View style={styles.partsRow}>
              <Text style={styles.partsLabel}>Part {partIndex + 1} of {parts.length}</Text>
              <View style={{ flexDirection: 'row', gap: 6 }}>
                {parts.map((p, i) => (
                  <TouchableOpacity key={p.name} onPress={() => setPartIndex(i)}>
                    <View style={[styles.partDot, i === partIndex && styles.partDotOn]} />
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}
        </View>

        {/* Title + meta */}
        <Text style={styles.title}>{video.title}</Text>
        <Text style={styles.meta}>
          {fmtCount(video.views)} view{video.views === 1 ? '' : 's'} · {fmtDuration(video.durationSeconds)}
          {video.createdAt ? ` · ${new Date(video.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
        </Text>

        {/* Owner row + actions */}
        <View style={styles.ownerRow}>
          <TouchableOpacity
            style={styles.ownerLeft}
            onPress={() => navigation.navigate('PublicProfile', { uid: video.owner?.uid })}
          >
            <View style={styles.ownerAvatar}>
              {video.owner?.avatarUrl ? (
                <Image source={{ uri: video.owner.avatarUrl }} style={styles.ownerAvatarImg} />
              ) : (
                <Text style={styles.ownerAvatarTxt}>
                  {String(video.owner?.userName || '?').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || '?'}
                </Text>
              )}
            </View>
            <View>
              <View style={styles.ownerNameRow}>
                <Text style={styles.ownerName}>{video.owner?.userName}</Text>
                {video.owner?.profileBadge === 'founder' && (
                  <Text style={styles.founderChip}>
                    FOUNDER{video.owner?.founderNumber ? ` #${video.owner.founderNumber}` : ''}
                  </Text>
                )}
              </View>
              <Text style={styles.ownerLink}>View profile ›</Text>
            </View>
          </TouchableOpacity>
          <View style={styles.actionRow}>
            {!interactionsLocked && (
            <TouchableOpacity
              style={[styles.pill, video.liked && styles.pillOn]}
              onPress={toggleLike}
              disabled={busy === 'like'}
            >
              <Ionicons name={video.liked ? 'thumbs-up' : 'thumbs-up-outline'} size={15} color={video.liked ? '#000' : '#FCD000'} />
              <Text style={[styles.pillTxt, video.liked && styles.pillTxtOn]}>{fmtCount(video.likes)}</Text>
            </TouchableOpacity>
            )}
            <TouchableOpacity style={styles.pill} onPress={shareVideo}>
              <Ionicons name="share-outline" size={15} color="#fff" />
              <Text style={styles.pillTxt}>Share</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Tags + description */}
        {(video.tags?.length > 0 || !!video.description || video.game) && (
          <View style={styles.descCard}>
            {(video.game || video.tags?.length > 0) && (
              <View style={styles.tagRow}>
                {!!video.game && <Text style={[styles.tag, styles.tagGold]}>{video.game}</Text>}
                {(video.tags || []).map((t) => <Text key={t} style={styles.tag}>#{t}</Text>)}
              </View>
            )}
            {!!video.description && <Text style={styles.desc}>{video.description}</Text>}
          </View>
        )}

        {/* Owner analytics (never public) */}
        {video.isOwner && video.watch && (
          <View style={styles.statsCard}>
            <Text style={styles.statsTitle}>Your video stats</Text>
            <View style={styles.statsRow}>
              <View style={styles.statCell}><Text style={styles.statV}>{fmtCount(video.views)}</Text><Text style={styles.statL}>VIEWS</Text></View>
              <View style={styles.statCell}><Text style={styles.statV}>{fmtCount(video.likes)}</Text><Text style={styles.statL}>LIKES</Text></View>
              <View style={styles.statCell}>
                <Text style={styles.statV}>{video.watch.avgWatchedPct !== null ? `${video.watch.avgWatchedPct}%` : '—'}</Text>
                <Text style={styles.statL}>AVG WATCHED</Text>
              </View>
            </View>
            {!video.published && <Text style={styles.privateNote}>This video is private — only you can see it. Publish it from My Videos.</Text>}
          </View>
        )}

        {/* Comments */}
        <Text style={styles.commentsTitle}>Comments ({video.comments || 0})</Text>
        {interactionsLocked ? (
          <Text style={styles.lockedNote}>Only members can like and comment on this video.</Text>
        ) : (
        <View style={styles.composer}>
          <TextInput
            style={styles.input}
            placeholder={getAuth().currentUser ? 'Add a comment…' : 'Sign in to comment…'}
            placeholderTextColor="#777"
            value={commentText}
            onChangeText={setCommentText}
            editable={!!getAuth().currentUser}
            maxLength={300}
            multiline
          />
          <TouchableOpacity
            style={[styles.sendBtn, (!commentText.trim() || busy === 'comment') && { opacity: 0.4 }]}
            onPress={submitComment}
            disabled={!commentText.trim() || busy === 'comment'}
          >
            {busy === 'comment'
              ? <ActivityIndicator size="small" color="#000" />
              : <Ionicons name="send" size={16} color="#000" />}
          </TouchableOpacity>
        </View>
        )}
        {comments.map((c) => (
          <View key={c.id} style={styles.comment}>
            <View style={styles.commentAvatar}>
              <Text style={styles.commentAvatarTxt}>
                {String(c.userName || '?').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || '?'}
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.commentWho}>
                {c.userName} <Text style={styles.commentWhen}>· {timeAgo(c.createdAt)}</Text>
              </Text>
              <Text style={styles.commentTxt}>{c.text}</Text>
            </View>
          </View>
        ))}

        <TouchableOpacity onPress={reportThis} style={{ marginTop: 20, alignSelf: 'center' }}>
          <Text style={styles.reportLink}>Report this video</Text>
        </TouchableOpacity>

        <View style={{ height: 40 }} />
      </ScrollView>
      <NavBar navigation={navigation} />
      {joinModal}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#0b0b0b' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  scroll: { padding: 16, maxWidth: 1100, width: '100%', alignSelf: 'center' },
  emptyTxt: { color: '#aaa', fontSize: 15, marginBottom: 16 },
  backBtn: { backgroundColor: '#1a1a1a', borderWidth: 1, borderColor: '#333', borderRadius: 8, paddingHorizontal: 18, paddingVertical: 10 },
  backBtnTxt: { color: '#FCD000', fontWeight: '800', fontSize: 13 },
  backLink: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  backLinkTxt: { color: '#FCD000', fontWeight: '700', fontSize: 14 },

  playerWrap: { marginBottom: 14 },
  playerFallback: { aspectRatio: 16 / 9, backgroundColor: '#000', borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  playerFallbackTxt: { color: '#777', fontSize: 13 },
  partsRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 },
  partsLabel: { color: '#aaa', fontSize: 12, fontWeight: '700' },
  partDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: '#333' },
  partDotOn: { backgroundColor: '#FCD000' },

  title: { color: '#fff', fontSize: 19, fontWeight: '800', lineHeight: 25 },
  meta: { color: '#aaa', fontSize: 13, marginTop: 4 },

  ownerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, flexWrap: 'wrap', gap: 10 },
  ownerLeft: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  ownerAvatar: { width: 38, height: 38, borderRadius: 19, backgroundColor: '#243A4A', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  ownerAvatarImg: { width: 38, height: 38, borderRadius: 19 },
  ownerAvatarTxt: { color: '#7FD4FF', fontWeight: '900', fontSize: 14 },
  ownerNameRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  ownerName: { color: '#fff', fontWeight: '800', fontSize: 14 },
  founderChip: {
    color: '#aff0ff', borderColor: 'rgba(0,207,255,0.55)', borderWidth: 1,
    backgroundColor: 'rgba(0,207,255,0.10)', fontSize: 9, fontWeight: '800',
    letterSpacing: 0.5, borderRadius: 20, paddingHorizontal: 7, paddingVertical: 2, overflow: 'hidden',
  },
  ownerLink: { color: '#aaa', fontSize: 11.5 },
  actionRow: { flexDirection: 'row', gap: 8 },
  pill: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#1a1a1a', borderWidth: 1, borderColor: '#333',
    borderRadius: 999, paddingHorizontal: 14, paddingVertical: 8,
  },
  pillOn: { backgroundColor: '#FCD000', borderColor: '#FCD000' },
  pillTxt: { color: '#fff', fontWeight: '700', fontSize: 13 },
  pillTxtOn: { color: '#000' },

  descCard: { backgroundColor: '#101010', borderWidth: 1, borderColor: '#242424', borderRadius: 12, padding: 14, marginTop: 14 },
  tagRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 8 },
  tag: {
    backgroundColor: '#1a1a1a', borderWidth: 1, borderColor: '#2e2e2e', color: '#aaa',
    fontSize: 11, fontWeight: '700', borderRadius: 999, paddingHorizontal: 9, paddingVertical: 3, overflow: 'hidden',
  },
  tagGold: { backgroundColor: '#FCD00018', borderColor: '#FCD00055', color: '#FCD000' },
  desc: { color: '#aaa', fontSize: 13, lineHeight: 19 },

  statsCard: { backgroundColor: '#101010', borderWidth: 1, borderColor: '#FCD00033', borderRadius: 12, padding: 14, marginTop: 14 },
  statsTitle: { color: '#fff', fontSize: 14, fontWeight: '800', marginBottom: 10 },
  statsRow: { flexDirection: 'row', gap: 8 },
  statCell: { flex: 1, backgroundColor: '#1a1a1a', borderWidth: 1, borderColor: '#2c2c2c', borderRadius: 8, padding: 10 },
  statV: { color: '#fff', fontSize: 17, fontWeight: '800' },
  statL: { color: '#aaa', fontSize: 9.5, letterSpacing: 0.6, marginTop: 2 },
  privateNote: { color: '#ffb84d', fontSize: 12, marginTop: 10, fontWeight: '600' },

  commentsTitle: { color: '#fff', fontSize: 15, fontWeight: '800', marginTop: 22, marginBottom: 10 },
  composer: { flexDirection: 'row', gap: 8, alignItems: 'flex-end' },
  input: {
    flex: 1, backgroundColor: '#1a1a1a', borderWidth: 1, borderColor: '#333',
    borderRadius: 10, color: '#fff', fontSize: 13, paddingHorizontal: 12, paddingVertical: 10, minHeight: 40,
  },
  sendBtn: { backgroundColor: '#FCD000', borderRadius: 10, width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  comment: { flexDirection: 'row', gap: 10, marginTop: 14 },
  commentAvatar: { width: 30, height: 30, borderRadius: 15, backgroundColor: '#3A2A0C', alignItems: 'center', justifyContent: 'center' },
  commentAvatarTxt: { color: '#FFB84D', fontWeight: '900', fontSize: 11 },
  commentWho: { color: '#fff', fontWeight: '800', fontSize: 12.5 },
  commentWhen: { color: '#777', fontWeight: '400' },
  commentTxt: { color: '#aaa', fontSize: 13, lineHeight: 18, marginTop: 2 },
  reportLink: { color: '#777', fontSize: 12, textDecorationLine: 'underline' },

  // Members-only (3.5a)
  lockedThumb: {
    width: '100%', aspectRatio: 16 / 9, backgroundColor: '#000', borderRadius: 10,
    alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: 14,
  },
  lockedThumbImg: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%', opacity: 0.35 },
  lockedBadge: {
    width: 52, height: 52, borderRadius: 26, backgroundColor: '#000000cc',
    borderWidth: 1, borderColor: '#FCD00088', alignItems: 'center', justifyContent: 'center',
  },
  lockedMsg: { color: '#ddd', fontSize: 14, lineHeight: 20, marginTop: 10 },
  lockedNote: { color: '#aaa', fontSize: 13, marginBottom: 4 },
  joinBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
    backgroundColor: '#FCD000', borderRadius: 999, paddingHorizontal: 18, paddingVertical: 9, marginTop: 14,
  },
  joinBtnTxt: { color: '#000', fontWeight: '800', fontSize: 13.5 },
});
